import { csvFormat } from 'd3-dsv';
import { chain, Dictionary, keyBy, keys, sortBy, uniq } from 'lodash';
import { AssayInfo, GetGroup } from './assays';
import { getRobustBioNumber, hasDuplicateBioNumbers } from './environments';
import { buildSortIdxByGene } from './export-utils';
import { getNormalisedValue } from './normalisation-mode';
import { CsvRow, CsvRows, FullSamplesByUID, NormalisationMode } from './types';
import { countDetectedGenesPerTarget, flattenRelevantAbundances, flattenSamplesByUID } from './utils';

export function getHeatmapCsvExportContent(
  samplesByUID: FullSamplesByUID,
  normalisationMode: NormalisationMode,
  getAssayInfo: (assay: string) => AssayInfo | undefined,
): string {
  return csvFormat(getCsvAssayRowsWithSamplesAsColumns(samplesByUID, normalisationMode, getAssayInfo));
}

function getCsvAssayRowsWithSamplesAsColumns(
  samplesByUID: FullSamplesByUID,
  normalisationMode: NormalisationMode,
  getAssayInfo: (assay: string) => AssayInfo | undefined,
): CsvRows {
  const samples = flattenSamplesByUID(samplesByUID);
  const abundanceBySampleIdAy = chain(samples)
    .keyBy(s => s.id)
    .mapValues(s => keyBy(s.abundances, a => a.assay))
    .value();
  const allAssayIds = samples.flatMap(s => s.abundances.map(r => r.assay));
  const assays = uniq(allAssayIds);
  if (allAssayIds.length !== assays.length * samples.length) {
    throw Error('All assays not analysed for all samples');
  }

  const hasDuplicateBioNums = hasDuplicateBioNumbers(samplesByUID);
  const unsortedRows = assays.reduce<CsvRows>((assayRowsAcc, assay) => {
    const ayInfo = getAssayInfo(assay);
    if (!ayInfo) {
      throw Error(`Unknown gene ${assay}`);
    }
    const group = ayInfo.l2Target;
    const assayRow = samples.reduce<CsvRow>(
      (rowColsAcc, sample) => {
        const abundance = abundanceBySampleIdAy[sample.id][assay];
        const bioRepNumber = getRobustBioNumber(sample, hasDuplicateBioNums);
        const value = getNormalisedValue(abundance, normalisationMode);
        rowColsAcc[bioRepNumber] = value
          ? normalisationMode === NormalisationMode.SIXTEEN_S
            ? value
            : value.toFixed(0)
          : undefined;
        return rowColsAcc;
      },
      { group, gene: ayInfo.gene, assay },
    );
    return [...assayRowsAcc, assayRow];
  }, []);

  const minimalAdequateGetGroup = (ay: string, _?: string) => getAssayInfo(ay)?.l2Target;
  const idxByGene = buildSortIdxByGene(samples, 'l2Target', minimalAdequateGetGroup);
  return sortBy(unsortedRows, row => (row.gene ? idxByGene[row.gene] : 0));
}

export function getNumberOfDetectedGenesByBioNumCsv(samplesByBioKey: FullSamplesByUID, getGroup: GetGroup): string {
  const sampleNumberColumn = 'Sample';
  const numDetectedColumn = 'Total number of detected genes';
  const descriptionColumn = 'Description';

  const samples = flattenSamplesByUID(samplesByBioKey);
  const hasDuplicateNums = hasDuplicateBioNumbers(samplesByBioKey);
  const rows = samples.map(sample => {
    const perTargetCounts = countDetectedGenesPerTarget([sample], getGroup);
    const sampleNum = getRobustBioNumber(sample, hasDuplicateNums);
    return keys(perTargetCounts).reduce<Dictionary<string | number>>(
      (acc, target) => {
        acc[target] = perTargetCounts[target];
        return acc;
      },
      {
        [sampleNumberColumn]: sampleNum,
        [descriptionColumn]: sample.environment.name,
        [numDetectedColumn]: uniq(
          flattenRelevantAbundances([sample])
            .filter(datum => !!datum.relative)
            .map(datum => datum.assay),
        ).length,
      },
    );
  });
  return csvFormat(rows);
}
